import React, { useEffect, useState, useCallback, useRef } from "react";
import { MessageCircle, Twitter, Share2 } from "lucide-react";
import { trackEvent } from "../lib/analytics";
import { useSignupBus } from "../lib/signupBus";
import { BACKEND_URL, PUBLIC_SITE } from "../lib/config";

const SHARE_MESSAGE = "Join me on the Xandi waitlist — beauty & wellness booking for Jamaica 💅";

const Waitlist = () => {
  const [role, setRole] = useState("customer");
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState({ tone: "", text: "" });
  const [refCode, setRefCode] = useState(null);
  const [refCount, setRefCount] = useState(0);
  const [copyLabel, setCopyLabel] = useState("Copy");
  const [refParam, setRefParam] = useState(null);
  const [socialProof, setSocialProof] = useState("");

  // Ensure we only fire the "loop complete" event once per session per ref_code
  const loopFiredFor = useRef(null);

  // Receive signups completed from the exit-intent modal → render referral panel here.
  const onBusSignup = useCallback((detail) => {
    if (!detail) return;
    if (detail.ref_code) {
      setRefCode(detail.ref_code);
      setRefCount(Math.max(0, (detail.referral_count | 0) || 0));
      setMsg({
        tone: "ok",
        text: "You're in! Check your email for your WELCOME15 code. 🎉",
      });
    }
  }, []);
  useSignupBus(onBusSignup);

  // Read ?ref=CODE
  useEffect(() => {
    const r = new URLSearchParams(window.location.search).get("ref");
    if (r) setRefParam(r);
  }, []);

  // Social proof
  useEffect(() => {
    const load = async () => {
      try {
        const r = await fetch(`${BACKEND_URL}/api/launch-stats`);
        if (r.ok) {
          const d = await r.json();
          const n = d.total_signups || 0;
          if (n > 0) {
            setSocialProof(
              `✨ Join ${n} ${n === 1 ? "early member" : "early members"} already on the list`
            );
          }
        }
      } catch (e) {
        /* silent */
      }
    };
    load();
  }, []);

  // Fire "Referral Loop Complete" once when count crosses 3
  useEffect(() => {
    if (refCode && refCount >= 3 && loopFiredFor.current !== refCode) {
      loopFiredFor.current = refCode;
      trackEvent("Referral Loop Complete", { ref_code: refCode, count: refCount });
    }
  }, [refCode, refCount]);

  const refreshRefStats = useCallback(async (code) => {
    try {
      const r = await fetch(
        `${BACKEND_URL}/api/launch-signup/stats?ref_code=${encodeURIComponent(code)}`
      );
      if (r.ok) {
        const d = await r.json();
        setRefCount(Math.max(0, d.referral_count | 0));
      }
    } catch (e) {
      /* silent */
    }
  }, []);

  const onSubmit = async (e) => {
    e.preventDefault();
    setMsg({ tone: "", text: "" });
    setBusy(true);
    try {
      const payload = {
        email: email.trim(),
        role,
        referred_by: refParam,
      };
      // Only send name/phone when present (Provider role surfaces these fields).
      const trimmedName = name.trim();
      const trimmedPhone = phone.trim();
      if (trimmedName) payload.name = trimmedName;
      if (trimmedPhone) payload.phone = trimmedPhone;

      const res = await fetch(`${BACKEND_URL}/api/launch-signup`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        const isNew = data.status !== "already_subscribed";
        setMsg({
          tone: "ok",
          text: isNew
            ? "You're in! Check your email for your WELCOME15 code. 🎉"
            : "You're already on the list — see you Dec 1! 💕",
        });
        // Fire conversion event only on a NEW signup (avoid double-counting).
        if (isNew) {
          trackEvent("Waitlist Signup", {
            role,
            referred: refParam ? "yes" : "no",
          });
        }
        if (data.ref_code) {
          setRefCode(data.ref_code);
          const initialCount = Math.max(0, (data.referral_count | 0) || 0);
          setRefCount(initialCount);
          refreshRefStats(data.ref_code);
          try { localStorage.setItem("xandi_signed_up", "1"); } catch (e) {}
        }
      } else {
        setMsg({
          tone: "err",
          text: data.detail
            ? typeof data.detail === "string"
              ? data.detail
              : "Please enter a valid email address."
            : "Please enter a valid email address.",
        });
      }
    } catch (err) {
      setMsg({ tone: "err", text: "Something went wrong. Please try again." });
    } finally {
      setBusy(false);
    }
  };

  const copyRef = () => {
    const value = `${PUBLIC_SITE}/?ref=${refCode}`;
    const finish = () => {
      setCopyLabel("Copied!");
      trackEvent("Referral Link Copied", { ref_code: refCode });
      setTimeout(() => setCopyLabel("Copy"), 2000);
    };
    const fallback = () => {
      try {
        const tmp = document.createElement("input");
        tmp.value = value;
        document.body.appendChild(tmp);
        tmp.select();
        document.execCommand("copy");
        document.body.removeChild(tmp);
        finish();
      } catch (e) {
        /* noop */
      }
    };
    if (navigator.clipboard) {
      navigator.clipboard.writeText(value).then(finish).catch(fallback);
    } else {
      fallback();
    }
  };

  const referralUrl = refCode ? `${PUBLIC_SITE}/?ref=${refCode}` : "";
  const shareText = `${SHARE_MESSAGE} ${referralUrl}`;

  const shareWhatsApp = () => {
    trackEvent("Referral Share", { method: "whatsapp", ref_code: refCode });
    // Use api.whatsapp.com directly — wa.me's redirect mangles 4-byte emojis (💅 → U+FFFD).
    const url = `https://api.whatsapp.com/send?text=${encodeURIComponent(shareText)}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const shareX = () => {
    trackEvent("Referral Share", { method: "x", ref_code: refCode });
    const url = `https://twitter.com/intent/tweet?text=${encodeURIComponent(
      SHARE_MESSAGE
    )}&url=${encodeURIComponent(referralUrl)}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const shareNative = async () => {
    trackEvent("Referral Share", { method: "native", ref_code: refCode });
    if (typeof navigator !== "undefined" && typeof navigator.share === "function") {
      try {
        await navigator.share({
          title: "Xandi Beauty & Wellness Hub",
          text: SHARE_MESSAGE,
          url: referralUrl,
        });
      } catch (e) {
        /* user cancelled — silent */
      }
    } else {
      // Desktop fallback: copy the full message + link to the clipboard
      const finish = () => {
        setCopyLabel("Copied!");
        setTimeout(() => setCopyLabel("Copy"), 2000);
      };
      if (navigator.clipboard) {
        navigator.clipboard.writeText(shareText).then(finish).catch(() => {});
      }
    }
  };

  const inRefMode = !!refCode;
  const progressPct = Math.min(100, (refCount / 3) * 100);
  const remaining = Math.max(0, 3 - refCount);
  const isProvider = role === "provider";

  return (
    <div className="signup" data-testid="waitlist-block">
      <div className="gl" />
      <h2>{isProvider ? "Grow your client base." : "Be first in line."}</h2>
      <p className="s">
        {isProvider ? (
          <>
            Apply to be a founding Xandi provider. We'll handle the bookings, payments, and
            promotion — you focus on your craft. Tell us how to reach you and we'll be in touch
            before launch with onboarding and your <strong>founder-provider</strong> perks.
          </>
        ) : (
          <>
            Join the Xandi waitlist today. We'll email you the moment booking opens — plus your{" "}
            <strong>WELCOME15</strong> code for 15% off.
          </>
        )}
      </p>
      <p
        className="s"
        data-testid="social-proof"
        style={{ fontWeight: 700, color: "#FFD9E6", marginBottom: 18, minHeight: 20 }}
      >
        {socialProof}
      </p>

      {!inRefMode && (
        <>
          <div className="toggle" role="tablist" data-testid="role-toggle">
            <button
              type="button"
              className={role === "customer" ? "active" : ""}
              onClick={() => setRole("customer")}
              data-testid="role-customer-toggle"
            >
              I'm a Customer
            </button>
            <button
              type="button"
              className={role === "provider" ? "active" : ""}
              onClick={() => setRole("provider")}
              data-testid="role-provider-toggle"
            >
              I'm a Provider
            </button>
          </div>

          {isProvider && (
            <div className="provider-fields" data-testid="provider-fields">
              <input
                type="text"
                placeholder="Your name (optional)"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="pf-input"
                autoComplete="name"
                data-testid="name-input"
              />
              <input
                type="tel"
                placeholder="Phone (optional, e.g. 876-555-0123)"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                className="pf-input"
                autoComplete="tel"
                data-testid="phone-input"
              />
            </div>
          )}

          {!isProvider && (
            <div className="customer-sms" data-testid="customer-sms">
              <input
                type="tel"
                placeholder="Phone (optional) — for SMS when doors open 🇯🇲"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                className="pf-input"
                autoComplete="tel"
                data-testid="customer-phone-input"
              />
            </div>
          )}

          <form className="form-row" onSubmit={onSubmit} data-testid="signup-form">
            <input
              type="email"
              placeholder="Enter your email address"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              data-testid="email-input"
            />
            <button
              type="submit"
              className="btn btn-primary"
              disabled={busy}
              data-testid="submit-button"
            >
              {busy ? "Joining…" : isProvider ? "Apply as Provider" : "Join Waitlist"}
            </button>
          </form>

          {isProvider && (
            <p
              className="provider-note"
              data-testid="provider-microcopy"
            >
              Founding providers get featured placement, zero listing fees for the first 90 days,
              and a verified badge.
            </p>
          )}
        </>
      )}

      <div className={`msg ${msg.tone}`} data-testid="form-message">
        {msg.text}
      </div>

      {inRefMode && (
        <div className="ref-panel" data-testid="referral-panel">
          <p style={{ fontSize: 17, color: "#fff", marginBottom: 6 }}>
            <strong>You're in! 🎉</strong>
          </p>
          <p className="s" style={{ marginBottom: 14 }}>
            Skip the line: when <strong>3 friends</strong> join with your link, you jump ahead in
            the queue.
          </p>
          <div className="ref-row">
            <input
              readOnly
              aria-label="Your referral link"
              className="ref-input"
              value={`${PUBLIC_SITE}/?ref=${refCode}`}
              data-testid="referral-link-input"
              onFocus={(e) => e.target.select()}
            />
            <button
              type="button"
              className="btn btn-primary"
              onClick={copyRef}
              data-testid="copy-ref-button"
            >
              {copyLabel}
            </button>
          </div>

          <div className="share-row" data-testid="share-row">
            <span className="share-label">Or share via</span>
            <div className="share-btns">
              <button
                type="button"
                className="share-btn share-wa"
                onClick={shareWhatsApp}
                aria-label="Share on WhatsApp"
                data-testid="share-whatsapp"
              >
                <MessageCircle size={18} strokeWidth={2.2} />
                <span>WhatsApp</span>
              </button>
              <button
                type="button"
                className="share-btn share-x"
                onClick={shareX}
                aria-label="Share on X"
                data-testid="share-x"
              >
                <Twitter size={18} strokeWidth={2.2} />
                <span>X</span>
              </button>
              <button
                type="button"
                className="share-btn share-native"
                onClick={shareNative}
                aria-label="Share via your device"
                data-testid="share-native"
              >
                <Share2 size={18} strokeWidth={2.2} />
                <span>Share</span>
              </button>
            </div>
          </div>
          <div style={{ marginTop: 18 }}>
            <div className="ref-bar-wrap">
              <div className="ref-bar" style={{ width: `${progressPct}%` }} />
            </div>
            <p
              style={{ marginTop: 12, fontSize: 15, color: "#fff" }}
              data-testid="referral-counter"
            >
              <span data-testid="ref-count">{refCount}</span> of 3 friends joined ·{" "}
              <span data-testid="ref-remaining">{remaining}</span> more to skip the line
            </p>
            {refCount >= 3 && (
              <p
                style={{
                  marginTop: 10,
                  fontSize: 14,
                  color: "#FFD9E6",
                  fontWeight: 700,
                }}
                data-testid="ref-complete-badge"
              >
                🎊 You've jumped the line — VIP launch access unlocked.
              </p>
            )}
            <p className="leaderboard-tease" data-testid="leaderboard-tease">
              🏆 Top referrer so far has <strong>7 friends</strong> on the list — can you beat that?
            </p>
          </div>
        </div>
      )}

      <p className="fineprint">
        By joining you agree to our{" "}
        <a href="/privacy" style={{ color: "#fff", textDecoration: "underline" }}>
          Privacy Policy
        </a>
        . We respect Jamaica's Data Protection Act (2020).
      </p>
    </div>
  );
};

export default Waitlist;
